// WalletTrackerTab — Tab 4: persönliche Watched-Wallets + Detail-View.
// Layout (zwei Spalten):
//   Links 256px: Wallet-Liste (CRUD + threshold + unread-Badge)
//   Rechts: Detail-View für selected Wallet
//     Header: Label + Adresse + Threshold + WS-Live-Status + Delete
//     4 KPI-Cards (Account / Margin Used / Free Margin / uPnL)
//     Sub-Sub-Tabs: Positionen / Trades 7d / Alerts
//     Tabelle nach aktiviertem Sub-Sub-Tab
//
// Daten: kommen aus useWalletMonitor (Phase 1 Hook) — Page übergibt
// hier vereinfachte Strukturen.

import { useState } from 'react';
import {
  Bell,
  BellOff,
  Check,
  Copy,
  ExternalLink as ExtIcon,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { KpiCard } from '@/components/ui/kpi-card';
import {
  DataTable,
  type DataTableColumn,
} from '@/components/ui/data-table';
import { useCredits } from '@/hooks/use-credits';

interface AddWalletDraft {
  address: string;
  label?: string | null;
  threshold_usd?: number;
}

interface UpdateWalletDraft {
  label?: string | null;
  threshold_usd?: number;
}

export interface WatchedWalletSummary {
  id: string;
  address: string;
  label: string;
  thresholdUsd: number;
  /** Anzahl ungesehener Alerts. */
  unreadCount: number;
  /** Letzter Update-Zeitstempel als „vor Xs/min/h"-String. */
  lastUpdate?: string;
  /** WS-live (true) oder REST-poll (false). */
  wsLive?: boolean;
}

export interface WalletAccountStats {
  accountValueUsd: number;
  marginUsedUsd: number;
  freeMarginUsd: number;
  unrealizedPnlUsd: number;
  /** % uPnL vom Account (für ROI-Sub-Line). */
  unrealizedPnlPct: number;
  /** Margin als % vom Account (für „X% von Account"-Sub-Line). */
  marginUtilizationPct: number;
  /** 24h Delta auf freeMargin (negativ = ausgegeben). */
  freeMargin24hDeltaUsd: number;
}

export interface WalletPositionRow {
  id: string;
  coin: string;
  side: 'LONG' | 'SHORT';
  /** Absolute Coin-Menge (immer positiv — Side kennt die Richtung). */
  size: number;
  entry: number;
  /** Break-Even-Preis: Entry +/− kumuliertes Funding pro Coin. */
  beUsd: number;
  mark: number;
  valueUsd: number;
  upnlUsd: number;
  /** Leverage-Wert (z.B. 40, 10) */
  leverage: number;
  /** „cross" oder „isolated" */
  leverageType: 'cross' | 'isolated';
  /** Funding-Cost (kumuliert seit Open). Negativ = Wallet hat Funding gezahlt. */
  fundingPaidUsd: number;
  /** Liquidations-Preis (USD). */
  liquidationPx: number | null;
}

type SubTab = 'positions' | 'trades' | 'alerts';

export interface LeaderboardPerfStats {
  /** Account-Value aus dem Leaderboard (kann von marginSummary abweichen — HL
   *  liefert hier den Gesamt-Account inkl. Spot). */
  accountValueUsd: number;
  day: { pnl: number; roi: number; vlm: number };
  week: { pnl: number; roi: number; vlm: number };
  month: { pnl: number; roi: number; vlm: number };
  allTime: { pnl: number; roi: number; vlm: number };
  /** EVE-Score (log10(av+1) × log10(vol+1) × (roi+1) für allTime). */
  eveScore: number | null;
}

export interface WalletAlarmsState {
  enabled: boolean;
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  toggle: () => void;
}

interface WalletTrackerTabProps {
  wallets: ReadonlyArray<WatchedWalletSummary>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Async — wirft bei Conflict/Invalid (Caller zeigt Error inline). */
  onAdd?: (input: AddWalletDraft) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
  /** Label / Threshold updaten (PATCH /eve/wallets/:id). */
  onUpdate?: (id: string, patch: UpdateWalletDraft) => Promise<void> | void;
  /** Browser-Notifications für getrackte Wallets ≥ Threshold. */
  alarms?: WalletAlarmsState;
  /** Stats für den selected wallet. null = noch nicht geladen. */
  stats: WalletAccountStats | null;
  positions: ReadonlyArray<WalletPositionRow>;
  positionsLoading?: boolean;
  /** Optional: Profitability-Stats aus dem HL-Leaderboard. null wenn Wallet
   *  nicht im Top-Leaderboard ist (= zu klein). */
  leaderboardPerf?: LeaderboardPerfStats | null;
}

export function WalletTrackerTab({
  wallets,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  onUpdate,
  alarms,
  stats,
  positions,
  positionsLoading,
  leaderboardPerf,
}: WalletTrackerTabProps) {
  const [subTab, setSubTab] = useState<SubTab>('positions');
  const [addOpen, setAddOpen] = useState(false);
  const { balance } = useCredits();
  const selected = wallets.find((w) => w.id === selectedId) ?? null;

  return (
    <div className="flex gap-3" style={{ minHeight: 600 }}>
      <WalletListPanel
        wallets={wallets}
        selectedId={selectedId}
        onSelect={onSelect}
        onAddClick={onAdd ? () => setAddOpen(true) : undefined}
        alarms={alarms}
      />

      {selected ? (
        <div className="flex flex-1 flex-col gap-3">
          <WalletHeader wallet={selected} onDelete={onDelete} onUpdate={onUpdate} />
          <WalletPerfStrip perf={leaderboardPerf ?? null} />
          <WalletKpis stats={stats} />
          <WalletDetailTabs
            wallet={selected}
            subTab={subTab}
            onSubTabChange={setSubTab}
            positions={positions}
            positionsLoading={positionsLoading}
          />
        </div>
      ) : (
        <div className="glass-strong grid flex-1 place-items-center rounded-2xl px-6 text-center text-sm text-neutral-500">
          {wallets.length === 0 ? (
            <div className="space-y-3">
              <p>Noch keine Wallets gewatched. Klick links auf + um eine hinzuzufügen.</p>
              {balance === 0 ? (
                <p className="text-[12px] text-amber-400">
                  Wallet-Tracking pausiert — dein Credit-Guthaben ist leer. Lade Credits auf, um es
                  fortzusetzen.
                </p>
              ) : (
                <p className="text-[12px] text-neutral-600">
                  Hinweis: Wallet-Tracking kostet täglich Credits. Bei leerem Guthaben wird es
                  automatisch pausiert.
                </p>
              )}
            </div>
          ) : (
            'Wähle links eine Wallet aus.'
          )}
        </div>
      )}

      {addOpen && onAdd ? (
        <AddWalletModal onClose={() => setAddOpen(false)} onAdd={onAdd} />
      ) : null}
    </div>
  );
}

// ── Add-Wallet Modal ─────────────────────────────────────────────────────────

function AddWalletModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (input: AddWalletDraft) => Promise<void> | void;
}) {
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [threshold, setThreshold] = useState('1000');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addressLower = address.trim().toLowerCase();
  const addrValid = /^0x[0-9a-f]{40}$/.test(addressLower);
  const thresholdNum = Number(threshold);
  const thresholdValid = Number.isFinite(thresholdNum) && thresholdNum >= 0;
  const canSubmit = addrValid && thresholdValid && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAdd({
        address: addressLower,
        label: label.trim() || null,
        threshold_usd: thresholdNum,
      });
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Hinzufügen fehlgeschlagen';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="glass-strong w-full max-w-md rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-amber-400">Wallet hinzufügen</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="text-neutral-500 hover:text-neutral-200"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label htmlFor="add-address" className="mb-1 block text-[11px] uppercase tracking-wider text-neutral-400">
              Adresse (0x…40 hex)
            </label>
            <input
              id="add-address"
              type="text"
              spellCheck={false}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={`w-full rounded-lg border bg-neutral-900/60 px-3 py-2 font-mono text-[13px] text-neutral-100 outline-none ${
                address.length === 0
                  ? 'border-white/10 focus:border-amber-500/40'
                  : addrValid
                    ? 'border-emerald-500/30 focus:border-emerald-500/50'
                    : 'border-rose-500/30 focus:border-rose-500/50'
              }`}
              placeholder="0x..."
            />
          </div>
          <div>
            <label htmlFor="add-label" className="mb-1 block text-[11px] uppercase tracking-wider text-neutral-400">
              Label (optional)
            </label>
            <input
              id="add-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[13px] text-neutral-100 outline-none focus:border-amber-500/40"
              placeholder="Whale1, ABC-Capital, …"
            />
          </div>
          <div>
            <label htmlFor="add-threshold" className="mb-1 block text-[11px] uppercase tracking-wider text-neutral-400">
              Alarm-Schwelle (USD)
            </label>
            <input
              id="add-threshold"
              type="number"
              min={0}
              step={100}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[13px] text-neutral-100 outline-none focus:border-amber-500/40"
            />
            <p className="mt-1 text-[11px] text-neutral-500">
              Alarm wenn diese Wallet eine Position ≥ Schwelle eröffnet.
            </p>
          </div>
          {error ? (
            <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
              {error}
            </div>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-3 py-1.5 text-[12px] text-neutral-300 hover:bg-white/5"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-[12px] font-bold text-neutral-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-neutral-700 disabled:text-neutral-500"
            >
              {submitting ? 'Speichert…' : 'Hinzufügen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Wallet-Liste Links ───────────────────────────────────────────────────────

function WalletListPanel({
  wallets,
  selectedId,
  onSelect,
  onAddClick,
  alarms,
}: {
  wallets: ReadonlyArray<WatchedWalletSummary>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAddClick?: () => void;
  alarms?: WalletAlarmsState;
}) {
  return (
    <div className="glass-strong flex w-64 shrink-0 flex-col overflow-hidden rounded-2xl">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
          👁 Deine Wallets
        </span>
        <div className="flex items-center gap-1.5">
          {alarms?.supported ? (
            <button
              type="button"
              onClick={alarms.toggle}
              aria-label={alarms.enabled ? 'Wallet-Alarme aus' : 'Wallet-Alarme an'}
              title={
                alarms.permission === 'denied'
                  ? 'Browser-Benachrichtigungen blockiert — Browser-Settings öffnen'
                  : alarms.enabled
                    ? 'Klick: Wallet-Alarme aus (gilt für alle getrackten Wallets nach jeweiliger Schwelle)'
                    : 'Klick: Wallet-Alarme an — Browser fragt nach Berechtigung'
              }
              className={
                alarms.enabled
                  ? 'grid size-6 place-items-center rounded-lg border border-amber-500/30 bg-amber-500/15 text-amber-400'
                  : 'grid size-6 place-items-center rounded-lg border border-white/10 text-neutral-500 hover:text-neutral-200'
              }
            >
              {alarms.enabled ? <Bell className="size-3" /> : <BellOff className="size-3" />}
            </button>
          ) : null}
          {onAddClick ? (
            <button
              type="button"
              onClick={onAddClick}
              aria-label="Wallet hinzufügen"
              className="grid size-6 place-items-center rounded-lg border border-amber-500/30 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
            >
              <Plus className="size-3" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="scroll-hidden flex-1 overflow-y-auto">
        {wallets.length === 0 ? (
          <p className="px-3 py-6 text-center text-[12px] text-neutral-500">
            Noch keine Wallets gewatched.
          </p>
        ) : (
          wallets.map((w) => {
            const active = w.id === selectedId;
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => onSelect(w.id)}
                className={`flex w-full items-center gap-2 border-b border-white/5 px-3 py-2.5 text-left hover:bg-white/5 ${
                  active ? 'bg-amber-500/10' : ''
                }`}
              >
                {w.unreadCount > 0 ? (
                  <span className="size-2 shrink-0 rounded-full bg-rose-400" />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium ${active ? 'text-amber-400' : ''}`}>
                    {w.label}
                  </div>
                  <div className="truncate font-mono text-[11px] text-neutral-500">
                    {shortAddr(w.address)}
                  </div>
                </div>
                {w.unreadCount > 0 ? (
                  <span className="text-[11px] font-bold text-rose-400">{w.unreadCount}</span>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Wallet-Header Rechts ─────────────────────────────────────────────────────

function WalletHeader({
  wallet,
  onDelete,
  onUpdate,
}: {
  wallet: WatchedWalletSummary;
  onDelete?: (id: string) => Promise<void> | void;
  onUpdate?: (id: string, patch: UpdateWalletDraft) => Promise<void> | void;
}) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [labelDraft, setLabelDraft] = useState(wallet.label);
  const [thresholdDraft, setThresholdDraft] = useState(String(wallet.thresholdUsd));
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function commitLabel() {
    const next = labelDraft.trim();
    setEditingLabel(false);
    if (!onUpdate || next === wallet.label) return;
    await onUpdate(wallet.id, { label: next.length > 0 ? next : null });
  }

  async function commitThreshold() {
    setEditingThreshold(false);
    const n = Number(thresholdDraft);
    if (!onUpdate || !Number.isFinite(n) || n < 0 || n === wallet.thresholdUsd) {
      setThresholdDraft(String(wallet.thresholdUsd));
      return;
    }
    await onUpdate(wallet.id, { threshold_usd: n });
  }

  return (
    <div className="glass-strong flex items-center gap-3 rounded-2xl px-4 py-3">
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {editingLabel ? (
            <input
              autoFocus
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={() => void commitLabel()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commitLabel();
                if (e.key === 'Escape') {
                  setLabelDraft(wallet.label);
                  setEditingLabel(false);
                }
              }}
              className="rounded border border-amber-500/40 bg-neutral-900/80 px-2 py-0.5 text-base font-bold text-amber-400 outline-none"
            />
          ) : (
            <h2 className="text-base font-bold text-amber-400">{wallet.label}</h2>
          )}
          {onUpdate && !editingLabel ? (
            <button
              type="button"
              onClick={() => {
                setLabelDraft(wallet.label);
                setEditingLabel(true);
              }}
              aria-label="Label umbenennen"
              className="text-neutral-500 hover:text-amber-400"
            >
              <Pencil className="size-3" />
            </button>
          ) : null}
          <span className="font-mono text-[12px] text-neutral-500">{shortAddr(wallet.address)}</span>
          <button
            type="button"
            onClick={() => void navigator.clipboard?.writeText(wallet.address)}
            aria-label="Adresse kopieren"
            className="text-neutral-500 hover:text-amber-400"
          >
            <Copy className="size-3.5" />
          </button>
          <a
            href={`https://hypurrscan.io/address/${wallet.address}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Wallet im Explorer öffnen"
            className="text-neutral-500 hover:text-amber-400"
          >
            <ExtIcon className="size-3.5" />
          </a>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-neutral-500">
          {editingThreshold ? (
            <div className="flex items-center gap-1">
              <span>Threshold:</span>
              <span className="text-amber-300">$</span>
              <input
                autoFocus
                type="number"
                min={0}
                step={100}
                value={thresholdDraft}
                onChange={(e) => setThresholdDraft(e.target.value)}
                onBlur={() => void commitThreshold()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void commitThreshold();
                  if (e.key === 'Escape') {
                    setThresholdDraft(String(wallet.thresholdUsd));
                    setEditingThreshold(false);
                  }
                }}
                className="w-20 rounded border border-amber-500/40 bg-neutral-900/80 px-1.5 py-0 text-amber-300 outline-none"
              />
              <button
                type="button"
                onClick={() => void commitThreshold()}
                aria-label="Threshold speichern"
                className="text-emerald-400 hover:text-emerald-300"
              >
                <Check className="size-3" />
              </button>
            </div>
          ) : (
            <span>
              Threshold:{' '}
              <strong className="text-amber-300">
                ${wallet.thresholdUsd.toLocaleString('en-US')}
              </strong>
              {onUpdate ? (
                <button
                  type="button"
                  onClick={() => setEditingThreshold(true)}
                  aria-label="Threshold ändern"
                  className="ml-1 text-neutral-500 hover:text-amber-400"
                >
                  <Pencil className="inline size-2.5" />
                </button>
              ) : null}
            </span>
          )}
          {wallet.lastUpdate ? (
            <>
              <span>·</span>
              <span>Last update: {wallet.lastUpdate}</span>
            </>
          ) : null}
          <span>·</span>
          <span className={wallet.wsLive ? 'text-emerald-400' : 'text-neutral-400'}>
            ● {wallet.wsLive ? 'WS Live' : 'REST'}
          </span>
        </div>
      </div>
      {onDelete ? (
        confirmDelete ? (
          <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1">
            <span className="text-[11px] text-rose-300">Wirklich löschen?</span>
            <button
              type="button"
              onClick={() => void onDelete(wallet.id)}
              className="text-[11px] font-bold text-rose-300 hover:text-rose-200"
            >
              Ja
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="text-[11px] text-neutral-400 hover:text-neutral-200"
            >
              Nein
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            aria-label="Wallet entfernen"
            className="text-neutral-500 hover:text-rose-400"
          >
            <Trash2 className="size-4" />
          </button>
        )
      ) : null}
    </div>
  );
}

// ── 4 KPI-Cards ──────────────────────────────────────────────────────────────

function WalletKpis({ stats }: { stats: WalletAccountStats | null }) {
  if (!stats) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <KpiCard key={i} label="—" value="—" sub="lädt…" />
        ))}
      </div>
    );
  }

  const upnlPositive = stats.unrealizedPnlUsd >= 0;
  const marginHigh = stats.marginUtilizationPct > 50;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label="Perps Equity"
        value={fmtBig(stats.accountValueUsd)}
        sub="Hyperliquid Perp-Account (ohne Spot/Staking)"
        tone="neutral"
      />
      <KpiCard
        label="Margin Used"
        value={fmtBig(stats.marginUsedUsd)}
        tone={marginHigh ? 'amber' : 'neutral'}
        icon={Zap}
        trend={marginHigh ? 'up' : null}
        sub={`${stats.marginUtilizationPct.toFixed(0)}% von Account`}
      />
      <KpiCard
        label="Free Margin"
        value={fmtBig(stats.freeMarginUsd)}
        tone="emerald"
        trend={stats.freeMargin24hDeltaUsd >= 0 ? 'up' : 'down'}
        sub={
          <>
            <span className={stats.freeMargin24hDeltaUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
              {stats.freeMargin24hDeltaUsd >= 0 ? '+' : '−'}
              {fmtBig(Math.abs(stats.freeMargin24hDeltaUsd))}
            </span>{' '}
            24h
          </>
        }
      />
      <KpiCard
        label="uPnL"
        value={(upnlPositive ? '+' : '−') + fmtBig(Math.abs(stats.unrealizedPnlUsd))}
        tone={upnlPositive ? 'emerald' : 'rose'}
        highlight
        icon={Sparkles}
        trend={upnlPositive ? 'up' : 'down'}
        sub={
          <>
            <span className={upnlPositive ? 'text-emerald-400' : 'text-rose-400'}>
              {upnlPositive ? '+' : ''}
              {stats.unrealizedPnlPct.toFixed(1)}%
            </span>{' '}
            ROI heute
          </>
        }
      />
    </div>
  );
}

// ── Sub-Sub-Tabs + Tabelle ───────────────────────────────────────────────────

function WalletDetailTabs({
  wallet,
  subTab,
  onSubTabChange,
  positions,
  positionsLoading,
}: {
  wallet: WatchedWalletSummary;
  subTab: SubTab;
  onSubTabChange: (t: SubTab) => void;
  positions: ReadonlyArray<WalletPositionRow>;
  positionsLoading?: boolean;
}) {
  const positionColumns: DataTableColumn<WalletPositionRow>[] = [
    {
      key: 'token',
      header: 'Token',
      cell: (r) => <span className="font-bold">{r.coin}</span>,
      toggleable: false,
    },
    {
      key: 'side',
      header: 'Side',
      cell: (r) => (
        <span
          className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${
            r.side === 'LONG'
              ? 'bg-emerald-500/15 text-emerald-400'
              : 'bg-rose-500/15 text-rose-400'
          }`}
        >
          {r.side}
        </span>
      ),
    },
    {
      key: 'lev',
      header: 'Lev.',
      align: 'right',
      cell: (r) => (
        <div className="text-right">
          <div className="font-bold">{r.leverage}×</div>
          <div className="text-[10px] text-neutral-500">({r.leverageType})</div>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      cell: (r) => <span className="font-bold text-amber-400">{fmtBig(r.valueUsd)}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      cell: (r) => fmtAmount(r.size, r.coin),
    },
    { key: 'entry', header: 'Entry', align: 'right', cell: (r) => `$${fmt(r.entry)}` },
    {
      key: 'be',
      header: 'BE',
      align: 'right',
      cell: (r) => <span className="text-neutral-400">${fmt(r.beUsd)}</span>,
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      cell: (r) => `$${fmt(r.mark)}`,
    },
    {
      key: 'pnl',
      header: 'PnL',
      align: 'right',
      cell: (r) => (
        <span className={r.upnlUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {r.upnlUsd >= 0 ? '' : '−'}
          {fmtFull(Math.abs(r.upnlUsd))}
        </span>
      ),
    },
    {
      key: 'funding',
      header: 'Funding',
      align: 'right',
      cell: (r) => (
        <span className={r.fundingPaidUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {r.fundingPaidUsd >= 0 ? '' : '−'}
          {fmtFull(Math.abs(r.fundingPaidUsd))}
        </span>
      ),
    },
    {
      key: 'liq',
      header: 'Liq.',
      align: 'right',
      cell: (r) =>
        r.liquidationPx !== null && r.liquidationPx > 0 ? (
          <span className="text-neutral-300">${fmt(r.liquidationPx)}</span>
        ) : (
          <span className="text-neutral-600">—</span>
        ),
    },
  ];

  return (
    <div className="glass-strong flex flex-1 flex-col overflow-hidden rounded-2xl">
      <div className="flex gap-1 border-b border-white/5 px-2 pt-2">
        <SubTabButton active={subTab === 'positions'} onClick={() => onSubTabChange('positions')}>
          📋 Positionen ({positions.length})
        </SubTabButton>
        <SubTabButton active={subTab === 'trades'} onClick={() => onSubTabChange('trades')}>
          📜 Trades 7d
        </SubTabButton>
        <SubTabButton active={subTab === 'alerts'} onClick={() => onSubTabChange('alerts')}>
          🚨 Alerts
          {wallet.unreadCount > 0 ? (
            <span className="ml-1 rounded bg-rose-500/15 px-1 text-[10px] text-rose-300">
              {wallet.unreadCount}
            </span>
          ) : null}
        </SubTabButton>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {subTab === 'positions' ? (
          <DataTable
            rows={positions}
            columns={positionColumns}
            rowKey={(r) => r.id}
            loading={positionsLoading}
            emptyState={`${wallet.label} hat aktuell keine offenen Positionen.`}
          />
        ) : subTab === 'trades' ? (
          <p className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-center text-sm text-neutral-500">
            Trade-Historie (7d) kommt in Phase 5 via userFills-Endpoint.
          </p>
        ) : (
          <p className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-center text-sm text-neutral-500">
            Alerts-Stream kommt in Phase 5 (Threshold-basierte Notifications).
          </p>
        )}
      </div>
    </div>
  );
}

function SubTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'flex items-center gap-1 rounded-t-md bg-amber-500/15 px-3 py-1.5 text-[12px] font-bold text-amber-300'
          : 'flex items-center gap-1 rounded-t-md px-3 py-1.5 text-[12px] font-bold text-neutral-500 hover:text-white'
      }
    >
      {children}
    </button>
  );
}

// ── Performance-Strip (aus HL-Leaderboard) ──────────────────────────────────

function WalletPerfStrip({ perf }: { perf: LeaderboardPerfStats | null }) {
  if (!perf) {
    return (
      <div className="glass rounded-2xl px-4 py-2 text-[11px] text-neutral-500">
        💤 Diese Wallet ist nicht im HL-Leaderboard (Top-10k) — keine Profitability-Stats verfügbar.
      </div>
    );
  }

  const tiles: ReadonlyArray<{
    window: 'day' | 'week' | 'month' | 'allTime';
    label: string;
  }> = [
    { window: 'day', label: '24h' },
    { window: 'week', label: '7d' },
    { window: 'month', label: '30d' },
    { window: 'allTime', label: 'All Time' },
  ];

  return (
    <div className="glass-strong rounded-2xl px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-neutral-400">
          📊 Profitability · HL-Leaderboard
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-neutral-500">
            Account: <span className="text-neutral-200 tabular">{fmtBig(perf.accountValueUsd)}</span>
          </span>
          {perf.eveScore !== null ? (
            <span className="text-neutral-500">
              ⚡ EVE-Score:{' '}
              <span className="text-amber-300 tabular">{perf.eveScore.toFixed(2)}</span>
            </span>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((t) => {
          const p = perf[t.window];
          const pnlTone = p.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400';
          const roiTone = p.roi >= 0 ? 'text-emerald-400' : 'text-rose-400';
          return (
            <div key={t.window} className="glass rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">{t.label}</div>
              <div className={`mt-1 text-base font-bold tabular ${pnlTone}`}>
                {p.pnl >= 0 ? '+' : '−'}
                {fmtBig(Math.abs(p.pnl))}
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-[11px]">
                <span className={roiTone}>
                  {p.roi >= 0 ? '+' : ''}
                  {(p.roi * 100).toFixed(1)}%
                </span>
                <span className="text-neutral-600">·</span>
                <span className="text-neutral-500">Vol {fmtBig(p.vlm)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function shortAddr(addr: string): string {
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-2)}`;
}

function fmt(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtBig(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

/** Full-Number-Format mit $ + Komma-Separator (für PnL/Funding-Spalten). */
function fmtFull(value: number): string {
  if (value === 0) return '$0';
  if (value < 1) return `$${value.toFixed(2)}`;
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

/** Coin-Amount formatieren — angemessene Decimals je nach Größenordnung. */
function fmtAmount(value: number, _coin: string): string {
  if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (value >= 1) return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
  return value.toFixed(4);
}
